import React, { useEffect, useState } from 'react';
import PlayerNameButton from '../../components/playerSheet/PlayerNameButton';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { sessionManager } from '../../config/sessionManager';
import { showErrorMsg, showSuccessMsg } from '../../config/common';

interface Player {
  _id: string;
  seasonPlayerId?: string;
  firstName: string;
  lastName: string;
  name?: string; // some APIs might already give combined name
  position?: string | string[];
  no: number; // fallback number field
  active?: boolean; // active status in the season
  masterActive?: boolean; // whether player is active in master players (setup)
  deleted?: boolean; // soft-deleted from setup players
}

const isGoaliePlayer = (player: Player) => {
  const positions = Array.isArray(player.position) ? player.position : player.position ? [player.position] : [];
  return positions.some((pos) => pos === 'Goalie');
};

const RosterPage: React.FC = () => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'no' | 'name' | 'position'>('no');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [startingGoalieId, setStartingGoalieId] = useState<string>('');
  const [savingGoalie, setSavingGoalie] = useState(false);

  const clientId = sessionManager.getClientId();
  const seasonId = sessionManager.getSeasonId();

  useEffect(() => {
    const fetchPlayers = async () => {
      setLoading(true);
      setError(null);

      try {
        if (!clientId || !seasonId) {
          setError('Client or Season not selected');
          setLoading(false);
          return;
        }
        // Get all players with existence flag for this season
        const [rosterRes, seasonRes] = await Promise.all([
          api.get(`${API_URLS.GET_RELATIVE_SEASON_PLAYER}`, {
            params: { clientId, seasonId },
          }),
          api.get(`${API_URLS.GET_SEASON_BY_ID}/${seasonId}`, {
            params: { clientId },
          }),
        ]);

        if (rosterRes.data.success) {
          const data: Player[] = rosterRes.data.data || [];
          setPlayers(data);
          setHasChanges(false);
        } else {
          setError(rosterRes.data.message || 'Failed to fetch players');
        }

        if (seasonRes.data.success && seasonRes.data.data) {
          setStartingGoalieId(seasonRes.data.data.startingGoalieId || '');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch players');
      } finally {
        setLoading(false);
      }
    };

    if (clientId && seasonId) {
      fetchPlayers();
    }
  }, [clientId, seasonId]);

  const goalieOptions = players.filter(
    (p) => p.active && !p.deleted && p.masterActive !== false && p.seasonPlayerId && isGoaliePlayer(p),
  );

  const handleStartingGoalieChange = async (seasonPlayerId: string) => {
    if (!clientId || !seasonId) return;
    const previous = startingGoalieId;
    setStartingGoalieId(seasonPlayerId);
    try {
      setSavingGoalie(true);
      const response = await api.post(API_URLS.UPDATE_SEASON_STARTING_GOALIE, {
        clientId,
        seasonId,
        startingGoalieId: seasonPlayerId || null,
      });
      if (response.data.success) {
        showSuccessMsg(seasonPlayerId ? 'Season goalie updated' : 'Season goalie cleared');
      } else {
        setStartingGoalieId(previous);
        showErrorMsg(response.data.message || 'Failed to update season goalie');
      }
    } catch (err: any) {
      setStartingGoalieId(previous);
      showErrorMsg(err.response?.data?.message || err.message || 'Failed to update season goalie');
    } finally {
      setSavingGoalie(false);
    }
  };

  const handleSort = (field: 'no' | 'name' | 'position') => {
    if (sortBy === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const filteredAndSortedPlayers = players
    .filter((p) => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      const displayName = (p.name || `${p.firstName || ''} ${p.lastName || ''}`).trim().toLowerCase();
      const position = Array.isArray(p.position)
        ? p.position.join(', ').toLowerCase()
        : (p.position || '').toLowerCase();
      return displayName.includes(term) || position.includes(term) || String(p.no).includes(term);
    })
    .sort((a, b) => {
      // Then sort by selected field
      let aComp: string | number = '';
      let bComp: string | number = '';
      if (sortBy === 'no') {
        aComp = a.no;
        bComp = b.no;
      } else if (sortBy === 'name') {
        aComp = (a.name || `${a.firstName || ''} ${a.lastName || ''}`).trim().toLowerCase();
        bComp = (b.name || `${b.firstName || ''} ${b.lastName || ''}`).trim().toLowerCase();
      } else if (sortBy === 'position') {
        const apos = Array.isArray(a.position) ? a.position.join(', ') : a.position || '';
        const bpos = Array.isArray(b.position) ? b.position.join(', ') : b.position || '';
        aComp = apos.toLowerCase();
        bComp = bpos.toLowerCase();
      }
      if (aComp < bComp) return sortOrder === 'asc' ? -1 : 1;
      if (aComp > bComp) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

  const handleToggleInclude = (id: string, checked: boolean) => {
    setPlayers((prev) => prev.map((p) => (p._id === id ? { ...p, active: checked } : p)));
    setHasChanges(true);
  };

  const handleSelectAll = (checked: boolean) => {
    // Only toggle season 'active' for players who are active in master players list
    setPlayers((prev) => prev.map((p) => (p.masterActive === false ? p : { ...p, active: checked })));
    setHasChanges(true);
  };

  const selectablePlayers = filteredAndSortedPlayers.filter((p) => p.masterActive !== false && !p.deleted);
  const allSelected = selectablePlayers.length > 0 && selectablePlayers.every((p) => p.active);

  const handleUpdateSeasonRoster = async () => {
    if (!clientId || !seasonId) return;
    try {
      setSaving(true);
      const playerIds = players.filter((p) => p.active).map((p) => p._id);

      const payload = {
        clientId,
        seasonId,
        playerIds,
      };

      await api.post(API_URLS.UPDATE_SEASON_PLAYER, payload);
      showSuccessMsg('Season roster updated successfully!');
      setHasChanges(false);
    } catch (err: any) {
      showErrorMsg(err.response?.data?.message || err.message || 'Failed to update season roster');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full mb-10 mt-6 px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="bg-white border border-[#bccedf] rounded-lg">
          <div className="flex items-center justify-center py-8">
            <div className="text-lg">Loading roster...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full mb-10 mt-6 px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="bg-white border border-[#bccedf] rounded-lg">
          <div className="flex items-center justify-center py-8">
            <div className="text-red-600 text-lg">Error: {error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mb-10 mt-6 px-4 sm:px-6 lg:px-8 max-w-7xl">
      <header className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#05396b]">Season Roster</h1>
        <p className="mt-2 text-sm sm:text-base text-[#05396b] font-medium max-w-3xl">
          Manage which players are included in this season. Toggle the checkbox to add or remove a player, then click
          Update Season Roster to save. Set the season goalie used as the default on new games.
        </p>
      </header>

      <div className="mb-6 bg-white border border-[#bccedf] rounded-lg p-4">
        <label className="block text-sm font-semibold text-[#05396b] mb-2">Season Goalie</label>
        <select
          value={startingGoalieId}
          onChange={(e) => handleStartingGoalieChange(e.target.value)}
          disabled={savingGoalie || goalieOptions.length === 0}
          className="w-full sm:w-96 px-4 py-2 border border-[#22c55e] rounded bg-[#eef5fc] focus:outline-none disabled:opacity-60"
        >
          <option value="">Select goalie...</option>
          {goalieOptions.map((g) => {
            const name = (g.name || `${g.firstName || ''} ${g.lastName || ''}`).trim();
            return (
              <option key={g.seasonPlayerId} value={g.seasonPlayerId}>
                #{g.no} {name}
              </option>
            );
          })}
        </select>
        {goalieOptions.length === 0 && (
          <p className="mt-2 text-sm text-gray-500">Add active Goalie-position players to the season roster first.</p>
        )}
      </div>

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <input
          type="text"
          placeholder="Search players..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full sm:w-80 px-4 py-2 border border-[#22c55e] rounded bg-[#eef5fc] focus:outline-none"
        />
        <div className="flex justify-end gap-3">
          <button
            onClick={() => handleSelectAll(!allSelected)}
            className="font-semibold py-3 px-6 rounded-md transition-colors duration-200 bg-[#05396b] text-white hover:bg-[#04305a] cursor-pointer"
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#bccedf] rounded-lg">
        <div className="bg-[#05396b] text-white text-xl font-semibold px-4 py-2 rounded-t-lg">
          Players ({filteredAndSortedPlayers.length})
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead>
              <tr className="bg-[#f4fafd]">
                <th
                  className="px-3 py-2 border-b text-center cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('no')}
                >
                  # {sortBy === 'no' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="px-3 py-2 border-b text-left cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}
                >
                  Name {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="px-3 py-2 border-b text-left cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('position')}
                >
                  Position {sortBy === 'position' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-3 py-2 border-b text-center">In Season</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedPlayers.map((player, index) => {
                const displayName = (player.name || `${player.firstName || ''} ${player.lastName || ''}`).trim();
                const posText = Array.isArray(player.position) ? player.position.join(', ') : player.position || '-';
                return (
                  <tr
                    key={player._id}
                    className={`${
                      index % 2 === 0 ? 'bg-white' : 'bg-green-50'
                    } ${player.deleted || player.active === false ? 'opacity-50' : ''}`}
                  >
                    <td className="px-3 py-1 border-b text-center font-semibold">{player.no}</td>
                    <td className="px-3 py-1 border-b font-medium">
                      {player.active && player.seasonPlayerId && seasonId ? (
                        <PlayerNameButton
                          playerSeasonId={player.seasonPlayerId}
                          seasonId={seasonId}
                          className="text-[#05396b] font-medium hover:underline"
                        >
                          {displayName}
                        </PlayerNameButton>
                      ) : (
                        displayName
                      )}
                      {player.deleted && <span className="ml-2 text-xs text-red-400 italic">(Removed)</span>}
                    </td>
                    <td className="px-3 py-1 border-b">{posText}</td>
                    <td className="px-3 py-1 border-b text-center">
                      <input
                        type="checkbox"
                        checked={!!player.active}
                        onChange={(e) => handleToggleInclude(player._id, e.target.checked)}
                        className="accent-green-600 w-5 h-5 cursor-pointer"
                        disabled={player.masterActive === false || !!player.deleted}
                        title={
                          player.deleted
                            ? 'Player has been removed from Setup'
                            : player.masterActive === false
                              ? 'Player is inactive in Setup and cannot be added to season'
                              : 'Toggle player in season'
                        }
                      />
                    </td>
                  </tr>
                );
              })}
              {filteredAndSortedPlayers.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    No players found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-center mt-6">
        <button
          onClick={handleUpdateSeasonRoster}
          disabled={!hasChanges || saving}
          className={`font-semibold py-3 px-6 rounded-md transition-colors duration-200 ${
            hasChanges && !saving ? 'bg-[#7fe526] cursor-pointer' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
          title={hasChanges ? 'Save roster changes' : 'No changes to save'}
        >
          {saving ? 'Updating...' : 'Update Season Roster'}
        </button>
      </div>
    </div>
  );
};

export default RosterPage;
